// API-CLIENT INTEGRATION - Community types from api-client

import { SortTypes } from '@unni/api-client/contracts/search'

export { FileAccessTypes, FileTypes, ImageContextTypes, ImageRepository } from '@unni/api-client'

// RE-EXPORT API-CLIENT CONTRACTS - Community contracts from api-client
export type {
  PostCommunityDocumentParam, // POST /community/document payload
  PostCommunityDocumentResponse, // POST /community/document response
  PostCommunityCommentParam, // POST /document/{id}/comment payload
  DocumentCommentView, // Comment view from api-client
  CommunityView, // Community view from api-client
  CommunityCommentView, // Community comment view from api-client
  ResponseOfSubscribeDocument, // Subscribe response from api-client
  ReportCommunityContentParam, // Report content param from api-client
  ContentsType, // Contents type enum from api-client
  ReportEntityType, // Report entity type enum from api-client
  ReportReason, // Report reason enum from api-client
  MemberView, // Member view from api-client
  MemberType, // Member type enum from api-client
  BlindHistoryView, // Blind history from api-client
  CommunityCategory, // Category from api-client
  DocumentStatus as ImportedDocumentStatus, // Document status enum from api-client (renamed to avoid conflict)
  CommunityDocument, // Community document from api-client
  CommunityRecommendationsView, // Community recommendations view from api-client
  CommunityWithCommentView, // Community with comment view from api-client
  MemberDoctorView, // Member doctor view from api-client
  CommunityMedicalTipView, // Community medical tip view from api-client
} from '@unni/api-client/contracts/community'

export type DocumentWriterType = 'USER' | 'HOSPITAL' | 'ADMIN' | 'DOCTOR' | 'UNKNOWN'

export enum CategoryId {
  MEDICAL_TIP = 0,
  FREE_TALK = 1,
  PLASTIC_SURGERY_QUESTION = 2,
  SERVICE_CENTER = 3,
  REVIEWS = 5,
  HOSPITAL_QUESTION = 11,
  ASK_DOCTOR = 13,
}

// CHEETAH SPECIFIC DOCUMENT STATUS (FOR BACKWARDS COMPATIBILITY)
export enum CheetahDocumentStatus {
  NOT_APPROVED, // 대기
  APPROVED, // 승인
  DELETED, // 삭제
  BLIND, // 임시숨김처리
  PEACE, // 피스 (평화로운 처리, 작성자만 볼 수 있음)
  APPROVE_REQ, // 승인요청
  DENIED, // 반려
  DENY_WAITING, // 반려대기중
}

// Main DocumentStatus export - using CheetahDocumentStatus for backward compatibility
export { CheetahDocumentStatus as DocumentStatus }

// Also export the API client version with a different name if needed
// export { ImportedDocumentStatus as ApiClientDocumentStatus }

export interface GetDocumentListResponse {
  id: number
  title?: string
  contents: string
  photos: string[]
  thumbnail: unknown
  writerId: number
  writerNickName: string
  writerLevel: number
  writerCountry: string
  writerProfile: string
  writerStatus: string
  writerDoctorId: number | null
  writerEngagementType: EngagementType | null
  writerFake: boolean
  hasThumbUp: boolean
  hasComment: boolean
  hasSubscribed: boolean
  subscriberCount: number
  commentCount: number
  thumbUpCount: number
  viewCount: number
  best: boolean
  notice: boolean
  edited: boolean
  hasDoctorComment: boolean
  category: {
    id: CategoryId
    name: string
  }
  categoryId: CategoryId
  categoryName: string
  lang: 'ko' | 'ja' | string | null
  translateResult: { contents: string } | null
  status: number
  createTime: number
  updateTime: number
}

export interface GetDocumentDetailResponse {
  id: number
  title?: string
  contents: string
  thumbnail: string | null
  photos: string[]
  thumbUpCount: number
  commentCount: number
  createTime: number
  updateTime: number
  writerId: number
  writerDoctorId: number | null
  writerNickName: string
  writerLevel: number
  writerProfile: string
  writerCountry: string
  writerEngagementType: EngagementType | null
  writerStatus: string
  hasThumbUp: boolean
  category: {
    id: CategoryId
    createTime: number
    updateTime: number
    status: number
    icon: any
    name: string
    isForAdminWriter: boolean
  }
  categoryName: string
  categoryId: CategoryId
  status: CheetahDocumentStatus
  updatable: boolean
  hasReported: boolean | null
  reportCount: number
  subscriberCount: number
  viewCount: number
  hasDoctorComment: boolean | null
  skipped: any
  lang: 'ko' | 'ja' | string | null
  translateResult: { contents: string } | null
  writerFake: boolean
  hasComment: boolean
  isNotice: boolean
  isBest: boolean
  hasSubscribed: boolean
  edited: boolean
  blindRequestDTO: any
  writer: {
    type: DocumentWriterType
    id: number
  }
}

export interface BlindRequestDTO {
  id: number
  requesterName: string
  blindReqTime: number
  restoreReqTime: number
  restoreRegistedTime: number
}

export type EngagementType = 'CHOOSING' | 'OPERATED'

export interface CommunityDocumentComment {
  id: number
  createTime: number
  updateTime: number
  status: CheetahDocumentStatus
  calleeId: number | null
  calleeNickName: string | null
  calleeLevel: number | null
  calleeFake: boolean
  replyCommentId: number | null
  contents: string
  thumbUpCount: number
  commentCount: number
  reportCount: number
  edited: boolean
  isBest: boolean
  blindRestoreTime: number | null
  lang: 'ko' | 'ja' | string | null
  translateResult: { contents: string } | null
  writerCountry: string
  hasThumbUp: boolean
  hasReported: boolean | null
  skipped: boolean | null
  writerNickName: string
  writerEngagementType: EngagementType | null
  documentWriterId: number
  documentId: number
  writerId: number
  writerFake: boolean
  writerDoctorId: number | null
  calleeDoctorId: number | null
  writerLevel: number
  writerProfile: string
  writerName: string
  localTextFieldName: string
  blindRequestDTO?: BlindRequestDTO
}

export type SortFilterType = 'updateTime' | 'hotRating' | 'createTime'

export type CommunityDocumentTopicType =
  | 'YEONGNAM'
  | 'BUSAN'
  | 'DAEGU'
  | 'JEONLA'
  | 'JAPANESE'
  | 'DOCTOR_QNA'
  | 'Gangnam'
  | 'Chungdam'
  | 'Seonreung'
  | 'Nonhyun'
  | 'Hongdae'
  | 'Jamsil'
  | 'Seomyun'
  | 'Haeundae'
  | 'Dongsungro'
  | 'Songdo'
  | 'Daejeon'
  | 'Seoulyeok'
  | 'Wangsipri'
  | 'Mokdong'
  | 'Jongro'
  | 'Sungnam'
  | 'Suwon'
  | 'Anyang'
  | 'Seocho'
  | 'CSAT' // 수능
  | 'FIRST_CARE' // 첫 시술

// SORT TYPES FOR iOS API SPEC COMPLIANCE
export type SortType = 'rcmd' | 'update' | 'new' | 'hot'

// CATEGORY MANAGEMENT TYPES
export interface CategoryPermissions {
  read: boolean
  write: boolean
}

export interface CategoryType {
  id: CategoryId
  name: string
  iconUrl: string
  permissions: CategoryPermissions
  isForAdminWriter: boolean
  createTime: number
  updateTime: number
  status: number
}

// DOCUMENT REQUEST/RESPONSE TYPES FOR iOS API SPEC
export interface DocumentListRequest {
  isBest?: boolean
  writtenByDoctor?: boolean
  hasComments?: boolean
  categoryIds?: string
  start?: number
  length: number
  keyword?: string
  topic?: CommunityDocumentTopicType
  languages?: string
  sort: SortTypes
}

export interface DocumentDetailRequest {
  documentId: number
  memberId?: number
}

export interface DocumentCreateRequest {
  contents: string
  categoryId: number
  photos?: string[]
  thumbnail?: string
}

export interface DocumentUpdateRequest {
  documentId: number
  contents?: string
  categoryId?: number
  photos?: string[]
  thumbnail?: string
}

// INTERACTION API TYPES
export interface ThumbUpRequest {
  documentId: number
  memberId: number
}

export interface ThumbUpResponse {
  documentId: number
  thumbUpCount: number
  hasThumbUp: boolean
}

export interface SubscribeRequest {
  documentId: number
  memberId: number
}

export interface SubscribeResponse {
  documentId: number
  subscriberCount: number
  hasSubscribed: boolean
}

// COMMENT API TYPES
export interface CommentListRequest {
  documentId: number
  start?: number
  length?: number
  sort?: 'createTime' | 'thumbUpCount'
}

export interface CommentCreateRequest {
  documentId: number
  contents: string
  calleeId?: number
  replyCommentId?: number
}

export interface CommentUpdateRequest {
  commentId: number
  contents: string
}

export interface CommentLikeRequest {
  commentId: number
  memberId: number
}

export interface CommentLikeResponse {
  commentId: number
  thumbUpCount: number
  hasThumbUp: boolean
}

// DOCTOR SPECIFIC TYPES
export interface DoctorCommunityRequest {
  doctorId: number
  start?: number
  length?: number
  sort?: SortFilterType
}

export interface DoctorCommentRequest {
  doctorId: number
  start?: number
  length?: number
  sort?: 'createTime'
}

// PERSONALIZED CONTENT TYPES
export interface PersonalizedIdsRequest {
  memberId: number
  count?: number
  excludeIds?: number[]
}

export interface PersonalizedIdsResponse {
  communityIds: number[]
  refreshToken?: string
}

// TRANSLATION TYPES
export interface TranslationRequest {
  text: string
  targetLanguage: 'ko' | 'ja' | 'en'
  sourceLanguage?: 'ko' | 'ja' | 'en'
}

export interface TranslationResponse {
  translatedText: string
  sourceLanguage: string
  targetLanguage: string
  confidence: number
}

export interface GetDocumentsParams {
  start?: number
  length?: number
  sort?: SortTypes
  isBest?: boolean
  hasComments?: boolean
  writtenByDoctor?: boolean
  categoryId?: number
  categoryIds?: string
  languages?: string

  keyword?: string
  topic?: CommunityDocumentTopicType
}

export interface DocumentByDoctor {
  communityId: number
  contents: string
  photos: string[]
  helpfulCount: number
  commentCount: number
  viewCount: number
}

export interface CommentByDoctor {
  id: number
  replyCommentId: number | null
  contents: string
}

export interface GetCommentsByDoctorContent {
  community: DocumentByDoctor
  comment: CommentByDoctor
}

export interface DocumentCategory {
  categoryId: number
  categoryName: string
  categoryIconUrl: string
}

export interface SpringPaginationStandardFetcherResponse<T> {
  content: T
  pageable: {
    sort: {
      unsorted: boolean
      sorted: boolean
      empty: boolean
    }
  }
  last: boolean
  totalPages: number
  totalElements: number
  sort: {
    unsorted: boolean
    sorted: boolean
    empty: boolean
  }
  number: number
  first: boolean
  numberOfElements: number
  size: number
  empty: boolean
}

export interface CommunityRecommendation {
  communityId: number
  contents: string
  totalCommentCount: number
  isDoctorCommentWritten: boolean
}

// HOT DOCUMENTS & WAITING DOCUMENTS TYPES
export interface HotDocumentsRequest {
  start?: number
  length: number
  categoryIds?: string
  period?: 'day' | 'week' | 'month'
}

export interface WaitingDocumentsRequest {
  start?: number
  length: number
  categoryIds?: string
  doctorSpecialty?: string
}

// ENHANCED DOCUMENT INTERFACE FOR iOS API SPEC
export interface EnhancedDocument extends GetDocumentListResponse {
  // Additional fields for iOS API compatibility
  blindRequestDTO?: BlindRequestDTO | null
  skipped?: boolean | null
  writer: {
    type: DocumentWriterType
    id: number
  }
}

// SUBSCRIPTION DOCUMENTS
export interface SubscribedDocumentsRequest {
  memberId: number
  start?: number
  length: number
  sort?: SortFilterType
}

// BULK OPERATIONS
export interface BulkOperationRequest {
  documentIds: number[]
  operation: 'thumbUp' | 'subscribe' | 'delete' | 'report'
  reason?: 'COMMERCIAL' | 'SEXUAL' | 'VIOLENCE' | 'PRIVACY' | 'REPEAT' | 'ETC'
}

export interface BulkOperationResponse {
  successful: number[]
  failed: {
    documentId: number
    error: string
  }[]
}

// API RESPONSE WRAPPERS
export interface ApiResponse<T> {
  data: T
  success: boolean
  message?: string
  error?: string
}

export interface PaginatedResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  size: number
  number: number
  first: boolean
  last: boolean
}

// CATEGORY QUERY TYPES
export interface CategoryQueryRequest {
  memberId?: number
  includePermissions?: boolean
}

export interface CategoryQueryResponse {
  categories: CategoryType[]
  userPermissions?: Record<CategoryId, CategoryPermissions>
}

// TYPE COMPATIBILITY AND CONVERSION UTILITIES
// Convert between Cheetah types and API-client types

export type CompatibleDocumentStatus = CheetahDocumentStatus

// Document type compatibility bridge
export interface CompatibleDocument extends Omit<GetDocumentListResponse, 'status'> {
  status: CompatibleDocumentStatus
}

// Comment type compatibility
export interface CompatibleComment extends Omit<CommunityDocumentComment, 'status'> {
  status: CompatibleDocumentStatus
}

// API Response adapters for iOS API spec compliance
export type DocumentListApiResponse = PaginatedResponse<GetDocumentListResponse>
export type DocumentDetailApiResponse = ApiResponse<GetDocumentDetailResponse>
export type DocumentCreateApiResponse = ApiResponse<any> // TODO: Import proper response type
export type CommentListApiResponse = PaginatedResponse<CommunityDocumentComment>
export type CategoryListApiResponse = ApiResponse<CategoryType[]>

// Search and filtering type unions
export type DocumentFilterKeys = keyof DocumentListRequest
export type CommentFilterKeys = keyof CommentListRequest
export type CategoryFilterKeys = keyof CategoryQueryRequest

// Generic API operation result types
export interface CommunityApiOperationResult<T> {
  data: T
  success: boolean
  error?: string
  message?: string
}

export type CommunityMutationResult<T> = CommunityApiOperationResult<T> & {
  isLoading: boolean
  mutate: () => Promise<T>
  reset: () => void
}

// Query hooks parameter types
export interface UseCommunityDocumentsOptions extends GetDocumentsParams {
  enabled?: boolean
  staleTime?: number
  gcTime?: number
}

export interface UseCommunityCommentsOptions extends Omit<CommentListRequest, 'documentId'> {
  enabled?: boolean
  staleTime?: number
  gcTime?: number
}

export interface UseCommunityDocumentOptions {
  enabled?: boolean
  staleTime?: number
  gcTime?: number
}

// Error handling types
export interface CommunityApiError {
  code: string
  message: string
  details?: Record<string, any>
}

export interface CommunityApiErrorResponse {
  error: CommunityApiError
  success: false
  status: number
}

// CONSTANTS AND ENUMS FOR iOS API SPEC COMPLIANCE

// API ENDPOINTS CONSTANTS - Moved to service layer for hardcoding
// Previously exported COMMUNITY_API_ENDPOINTS constant has been removed
// Endpoints are now hardcoded directly in service methods

// QUERY KEYS FOR REACT QUERY
export const COMMUNITY_QUERY_KEYS = {
  all: ['community'] as const,
  documents: () => [...COMMUNITY_QUERY_KEYS.all, 'documents'] as const,
  documentsList: (filters?: GetDocumentsParams) => [...COMMUNITY_QUERY_KEYS.documents(), 'list', filters] as const,
  documentsInfinite: (filters?: DocumentListRequest) =>
    [...COMMUNITY_QUERY_KEYS.documents(), 'infinite', filters] as const,
  documentsHot: (filters?: HotDocumentsRequest) => [...COMMUNITY_QUERY_KEYS.documents(), 'hot', filters] as const,
  documentsWaiting: (filters?: WaitingDocumentsRequest) =>
    [...COMMUNITY_QUERY_KEYS.documents(), 'waiting', filters] as const,
  documentsSubscribed: (memberId: number, filters?: SubscribedDocumentsRequest) =>
    [...COMMUNITY_QUERY_KEYS.documents(), 'subscribed', memberId, filters] as const,

  document: (id: number) => [...COMMUNITY_QUERY_KEYS.all, 'document', id] as const,
  documentDetail: (id: number) => [...COMMUNITY_QUERY_KEYS.document(id), 'detail'] as const,

  comments: () => [...COMMUNITY_QUERY_KEYS.all, 'comments'] as const,
  commentsList: (documentId: number, filters?: CommentListRequest) =>
    [...COMMUNITY_QUERY_KEYS.comments(), 'list', documentId, filters] as const,

  categories: () => [...COMMUNITY_QUERY_KEYS.all, 'categories'] as const,
  categoriesList: (filters?: CategoryQueryRequest) => [...COMMUNITY_QUERY_KEYS.categories(), 'list', filters] as const,

  doctor: (id: number) => [...COMMUNITY_QUERY_KEYS.all, 'doctor', id] as const,
  doctorDocuments: (id: number, filters?: DoctorCommunityRequest) =>
    [...COMMUNITY_QUERY_KEYS.doctor(id), 'documents', filters] as const,
  doctorComments: (id: number, filters?: DoctorCommentRequest) =>
    [...COMMUNITY_QUERY_KEYS.doctor(id), 'comments', filters] as const,

  personalized: (memberId: number) => [...COMMUNITY_QUERY_KEYS.all, 'personalized', memberId] as const,
  personalizedIds: (memberId: number, filters?: PersonalizedIdsRequest) =>
    [...COMMUNITY_QUERY_KEYS.personalized(memberId), 'ids', filters] as const,

  medicalTips: () => [...COMMUNITY_QUERY_KEYS.all, 'medical-tips'] as const,
  recommendations: () => [...COMMUNITY_QUERY_KEYS.all, 'recommendations'] as const,
} as const

// PAGINATION DEFAULTS
export const PAGINATION_DEFAULTS = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  INFINITE_SCROLL_THRESHOLD: 0.8,
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  CACHE_TIME: 10 * 60 * 1000, // 10 minutes (deprecated)
  GC_TIME: 10 * 60 * 1000, // 10 minutes (new React Query v4+)
} as const

// SORT OPTIONS FOR UI
export const SORT_OPTIONS = {
  RECOMMENDED: 'rcmd',
  LATEST: 'update',
  NEWEST: 'new',
  HOT: 'hot',
} as const

export const SORT_LABELS = {
  [SORT_OPTIONS.RECOMMENDED]: '추천순',
  [SORT_OPTIONS.LATEST]: '최신순',
  [SORT_OPTIONS.NEWEST]: '등록순',
  [SORT_OPTIONS.HOT]: '인기순',
} as const

// CATEGORY PERMISSIONS
export enum CategoryPermission {
  READ = 'READ',
  WRITE = 'WRITE',
  ADMIN = 'ADMIN',
}

// API RESPONSE STATUS CODES
export enum ApiStatusCode {
  SUCCESS = 200,
  CREATED = 201,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
  INTERNAL_SERVER_ERROR = 500,
}

// CONTENT MODERATION STATUS
export enum ModerationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  FLAGGED = 'FLAGGED',
}

// INTERACTION TYPES
export enum InteractionType {
  THUMB_UP = 'THUMB_UP',
  SUBSCRIBE = 'SUBSCRIBE',
  COMMENT = 'COMMENT',
  REPORT = 'REPORT',
  SHARE = 'SHARE',
}

// VALIDATION RULES
export const VALIDATION_RULES = {
  DOCUMENT: {
    MIN_CONTENT_LENGTH: 10,
    MAX_CONTENT_LENGTH: 5000,
    MAX_PHOTOS: 10,
    ALLOWED_PHOTO_TYPES: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  },
  COMMENT: {
    MIN_CONTENT_LENGTH: 1,
    MAX_CONTENT_LENGTH: 1000,
    MAX_DEPTH: 3, // Reply depth limit
  },
  SEARCH: {
    MIN_KEYWORD_LENGTH: 2,
    MAX_KEYWORD_LENGTH: 100,
  },
} as const
